import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, BellOff, Smartphone, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getPushState, enablePush, disablePush } from "@/lib/push";

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();
  const prev = useRef(null);
  const [pushState, setPushState] = useState("default");
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ jobs: true, messages: true, announcements: true });
  const [quiet, setQuiet] = useState(null);
  useEffect(() => {
    getPushState().then(setPushState);
    api.get("/profile/notification-prefs").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/profile/quiet-hours").then((r) => setQuiet(r.data)).catch(() => {});
  }, []);

  const saveQuiet = async (patch) => {
    const next = { ...quiet, ...patch };
    setQuiet(next);
    try {
      const { data } = await api.put("/profile/quiet-hours", { use_default: next.use_default, enabled: next.enabled, start: next.start, end: next.end });
      setQuiet(data);
    } catch (e) { toast.error("Couldn't save quiet hours"); }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/profile/notification-prefs", next); }
    catch (e) { toast.error("Couldn't save preference"); }
  };

  const togglePush = async () => {
    try {
      if (pushState === "subscribed") { await disablePush(); setPushState("default"); toast("Push notifications turned off"); }
      else { await enablePush(); setPushState("subscribed"); toast.success("Push on — we'll alert you even when the app is closed"); }
    } catch (e) {
      if (e.message === "denied") toast.error("Notifications are blocked. Enable them in your browser settings for this site.");
      else toast.error("Couldn't enable push on this device.");
    }
  };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      if (prev.current !== null && data.count > prev.current) {
        try {
          const list = await api.get("/notifications");
          if (list.data[0]) toast(list.data[0].title, { description: list.data[0].body });
        } catch (e) { console.error("Failed to load notifications:", e); }
      }
      prev.current = data.count;
      setCount(data.count);
    } catch (e) { console.error("Notification poll failed:", e); }
  }, []);

  useEffect(() => { poll(); const t = setInterval(poll, 10000); return () => clearInterval(t); }, [poll]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        await api.post("/notifications/read-all");
        prev.current = 0;
        setCount(0);
      } catch (e) { console.error("Failed to open notifications:", e); }
    }
  };

  const click = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle} className="relative text-zinc-400 hover:text-[#FF5722] transition-colors p-1">
        <Bell size={20} />
        {count > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-[#FF5722] text-black text-[9px] font-mono font-black w-4 h-4 flex items-center justify-center border border-black">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto bg-[#141417] border-2 border-[#FF5722] shadow-[4px_4px_0px_#000] z-[60]" data-testid="notif-dropdown">
          <div className="p-3 border-b-2 border-zinc-800 flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">Notifications</span>
            <div className="flex items-center gap-3">
              <PushControl state={pushState} onToggle={togglePush} />
              <button data-testid="alert-settings-btn" onClick={() => setShowSettings((s) => !s)} className={`${showSettings ? "text-[#FF5722]" : "text-zinc-500"} hover:text-[#FF5722]`} title="Alert settings"><Settings size={14} /></button>
            </div>
          </div>
          {showSettings && (
            <div className="p-3 border-b-2 border-zinc-800 bg-black/40 space-y-2" data-testid="alert-settings-panel">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Which push alerts you get</div>
              {[["jobs", "Job updates"], ["messages", "New messages"], ["announcements", "Announcements"]].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between gap-2 text-xs text-zinc-300 cursor-pointer">
                  <span>{label}</span>
                  <input type="checkbox" data-testid={`pref-${k}`} checked={!!prefs[k]} onChange={() => togglePref(k)} className="w-4 h-4 accent-[#FF5722]" />
                </label>
              ))}
              {quiet && (
                <div className="pt-2 mt-1 border-t border-zinc-800 space-y-2" data-testid="my-quiet-hours">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Your quiet hours</div>
                  <label className="flex items-center justify-between gap-2 text-xs text-zinc-300 cursor-pointer">
                    <span>Use company default{quiet.global ? ` (${String(quiet.global.start).padStart(2, "0")}:00–${String(quiet.global.end).padStart(2, "0")}:00)` : ""}</span>
                    <input type="checkbox" data-testid="my-quiet-default" checked={!!quiet.use_default} onChange={(e) => saveQuiet({ use_default: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" />
                  </label>
                  {!quiet.use_default && (
                    <>
                      <label className="flex items-center justify-between gap-2 text-xs text-zinc-300 cursor-pointer">
                        <span>Mute non-urgent overnight</span>
                        <input type="checkbox" data-testid="my-quiet-enabled" checked={!!quiet.enabled} onChange={(e) => saveQuiet({ use_default: false, enabled: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" />
                      </label>
                      {quiet.enabled && (
                        <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
                          <span>From</span>
                          <select data-testid="my-quiet-start" value={quiet.start} onChange={(e) => saveQuiet({ use_default: false, start: parseInt(e.target.value) })} className="bg-zinc-900 border border-zinc-700 text-white text-xs px-1 py-0.5">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
                          <span>to</span>
                          <select data-testid="my-quiet-end" value={quiet.end} onChange={(e) => saveQuiet({ use_default: false, end: parseInt(e.target.value) })} className="bg-zinc-900 border border-zinc-700 text-white text-xs px-1 py-0.5">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="p-4 text-zinc-600 text-sm font-mono">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => click(n)}
                className={`w-full text-left p-3 border-b border-zinc-800 hover:bg-zinc-900 transition-colors ${!n.read ? "bg-zinc-900/50" : ""}`}>
                <div className="text-white text-sm font-semibold">{n.title}</div>
                {n.body && <div className="text-zinc-500 text-xs mt-0.5 line-clamp-2">{n.body}</div>}
                <div className="font-mono text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function PushControl({ state, onToggle }) {
  if (state === "unsupported") return null;
  if (state === "ios-install")
    return <span data-testid="push-ios-hint" className="flex items-center gap-1 font-mono text-[9px] text-zinc-500" title="On iPhone/iPad, tap Share → Add to Home Screen, then open the app to enable alerts."><Smartphone size={11} /> Add to Home Screen</span>;
  if (state === "denied")
    return <span data-testid="push-blocked" className="flex items-center gap-1 font-mono text-[9px] text-zinc-500" title="Notifications are blocked in your browser settings for this site."><BellOff size={11} /> Blocked</span>;
  if (state === "subscribed")
    return <button data-testid="push-toggle" onClick={onToggle} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-emerald-400 hover:text-emerald-300"><BellRing size={12} /> Alerts on</button>;
  return <button data-testid="push-toggle" onClick={onToggle} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-[#FF5722] hover:underline"><BellRing size={12} /> Enable alerts</button>;
}
